/**
 * Раздел «Врачи».
 *
 * Устроен ровно как «Клиники» и рисуется тем же кодом (`components/Cards.tsx`):
 * плитки в сетке, подробности в окне, ПРОСМОТР и ПРАВКА — разные состояния,
 * включаемые разными кнопками. Это два взгляда на одну связь (§3), работа с
 * ними идёт вперемешку, и разное оформление заставляло бы каждый раз заново
 * соображать, где кнопка правки и что значит цвет.
 *
 * Прежде здесь была таблица с правкой по щелчку: попытка посмотреть врача
 * попадала по ФИО и превращала его в поле ввода, а редкие поля (Telegram,
 * WhatsApp, заметки) прятались в раскрывающуюся строку. В карточке они просто
 * помещаются.
 *
 * Связь врач↔клиника правится и отсюда, и из раздела «Клиники» (§3).
 * Уведомления выключены, пока не указана почта: уведомлять некуда.
 */

import { useMemo, useState } from 'react';
import {
  Check,
  Mail,
  MessageCircle,
  Pencil,
  Phone,
  Plus,
  Send,
  StickyNote,
  Archive,
  ArchiveRestore,
  Search,
} from 'lucide-react';
import { formatPhone } from '@otc/shared';
import { useBootstrap } from '../api/queries.js';
import { Messengers } from '../components/Messengers.js';
import { useDrafts } from '../store/drafts.js';
import { useSync } from '../store/useSync.js';
import { useModule } from '../store/useModule.js';
import { Button, Field, SectionHeader, Spinner, Toast, useToast } from '../components/ui.js';
import { CardWindow } from '../components/CardWindow.js';
import { ConflictModal } from '../components/GuardModals.js';
import { ConfirmDialog } from '../components/ConfirmDialog.js';
import { SaveDiscard } from '../components/SaveDiscard.js';
import { Card, CardChips, CardFact, CardGrid, CardLinks, Chip, Row, Пометка } from '../components/Cards.js';
import { DoctorPortalBlock } from './DoctorPortalBlock.js';
import { DoctorCabinetPreview } from './DoctorCabinetPreview.js';
import { ToggleSetting } from '../components/Toggle.js';
import { ClinicPicker } from '../components/ClinicPicker.js';
import { MailingDialog } from '../components/MailingDialog.js';
import { ClinicIcon, DoctorIcon } from '../components/icons.js';
import { useFillHeight } from '../components/useFillHeight.js';
import { useTableZoom } from '../components/useTableZoom.js';
import { ZoomControl } from '../components/ZoomControl.js';

interface DoctorRow {
  id: string;
  version: number;
  name: string;
  clinics: string[];
  phone: string;
  email: string;
  telegram: string;
  whatsapp: string;
  max: string;
  notify: boolean;
  note: string;
  /** Полное ФИО по частям — заполняется вручную, показывается в карточке. */
  lastName?: string;
  firstName?: string;
  middleName?: string;
  /** В архиве: врач с заказами, которого «удалили». */
  archived: boolean;
  /** Видит ли врач цены в своём кабинете. */
  showPrices?: boolean;
}

/**
 * Короткое имя из частей: «Аюшеева Галина Сергеевна» → «Аюшеева Г.С.».
 *
 * Им подписаны заказы, счета, письма и плитки. Собирается само, чтобы не
 * вводить фамилию дважды и не получить однажды две разные.
 *
 * Пустая фамилия — пустое имя: «Г.С.» без фамилии не опознать.
 */
function короткоеИмя(врач: { lastName?: string; firstName?: string; middleName?: string }): string {
  const фамилия = (врач.lastName ?? '').trim();
  if (!фамилия) return '';
  const буква = (часть?: string) => {
    const т = (часть ?? '').trim();
    return т ? `${т[0]!.toUpperCase()}.` : '';
  };
  return `${фамилия} ${буква(врач.firstName)}${буква(врач.middleName)}`.trim();
}

/**
 * Правка любой части ФИО — вместе с пересборкой короткого имени.
 *
 * Короткое имя переписывается ТОЛЬКО когда есть фамилия: у старых записей,
 * где части ещё не заполнены, прежнее имя должно остаться нетронутым —
 * иначе врач в заказах на мгновение стал бы безымянным.
 */
function обновитьФИО(
  врач: { name: string; lastName?: string; firstName?: string; middleName?: string },
  onUpdate: (patch: Record<string, unknown>) => void,
  часть: { lastName?: string; firstName?: string; middleName?: string },
): void {
  const новый = { ...врач, ...часть };
  const коротко = короткоеИмя(новый);
  onUpdate(коротко ? { ...часть, name: коротко } : часть);
}

/**
 * Полное ФИО врача одной строкой: «Аюшеева Галина Сергеевна».
 *
 * Пусто, если фамилия не заполнена: «Галина Сергеевна» без фамилии — не
 * обращение и не подпись, а половина сведений, и показывать её вместо
 * привычного короткого имени хуже, чем не показывать вовсе.
 */
function полноеФИО(врач: { lastName?: string; firstName?: string; middleName?: string }): string {
  const фамилия = (врач.lastName ?? '').trim();
  if (!фамилия) return '';
  /*
   * Одна буква — это инициал, и он пишется с точкой: «Аюшеева Г.С.».
   * Пока полные имя и отчество не вписаны, в заголовке стоит привычное
   * короткое написание, а не «Аюшеева Г С».
   */
  const часть = (значение?: string) => {
    const т = (значение ?? '').trim();
    return т.length === 1 ? `${т.toUpperCase()}.` : т;
  };
  const имя = часть(врач.firstName);
  const отчество = часть(врач.middleName);
  const инициалы = имя.endsWith('.') && отчество.endsWith('.');
  return инициалы
    ? `${фамилия} ${имя}${отчество}`
    : [фамилия, имя, отчество].filter(Boolean).join(' ');
}

export function Doctors() {
  const bootstrap = useBootstrap(true);
  const module = useModule('doctors');
  const { toast, show } = useToast();

  /** Открытая карточка. Подробности живут в окне, а не в плитке. */
  const [openId, setOpenId] = useState<string | null>(null);
  /** Карточки в режиме правки. Просмотр и правка — разные состояния. */
  const [editing, setEditing] = useState<Record<string, boolean>>({});
  const [query, setQuery] = useState('');
  const [confirmDoctor, setConfirmDoctor] = useState<string | null>(null);
  /** Окно массовой рассылки: объявления лаборатории всем врачам сразу. */
  const [mailingOpen, setMailingOpen] = useState(false);
  /** Показан архив вместо рабочего списка. */
  const [showArchive, setShowArchive] = useState(false);
  /** Чей кабинет открыт на просмотр — предпросмотр глазами врача. */
  const [cabinetFor, setCabinetFor] = useState<string | null>(null);

  useSync('doctors', 'doctor', bootstrap.data?.doctors as never);

  const draftState = useDrafts((state) => state.modules.doctors);
  const doctors = useMemo(() => {
    void draftState;
    return useDrafts.getState().entities<DoctorRow>('doctors', 'doctor');
  }, [draftState]);

  const clinics = bootstrap.data?.clinics ?? [];
  const update = (id: string, patch: Partial<DoctorRow>) =>
    useDrafts.getState().update('doctors', 'doctor', id, patch);

  /**
   * Сохранение раздела.
   *
   * `оставитьПравку` — для кнопки в самой карточке: после сохранения она
   * должна остаться открытой и в том же состоянии, иначе правка обрывается
   * на полуслове. Кнопка в шапке, наоборот, завершает работу со списком и
   * режим правки выключает.
   */
  const save = async ({ оставитьПравку = false }: { оставитьПравку?: boolean } = {}): Promise<boolean> => {
    const outcome = await module.save();
    if (outcome.ok) {
      /* Молча: сохранение — не событие, о котором стоит сообщать. */
      if (!оставитьПравку) setEditing({});
      return true;
    }
    if (outcome.blocked) show(outcome.blocked[0]!.message, 'error');
    else if (outcome.error) show(outcome.error, 'error');
    return false;
  };

  /**
   * «Готово» в карточке: сохранить и выйти из правки.
   *
   * Раньше здесь стояли две кнопки рядом — «Готово» и «Сохранить», — и это
   * было два нажатия на одно намерение: закончил править. Теперь «Готово»
   * само отправляет правки в базу.
   *
   * Если сохранить не удалось (расхождение версий, отказ сервера), карточка
   * ОСТАЁТСЯ в правке: иначе правки исчезли бы с глаз, оставшись
   * несохранёнными.
   */
  const завершитьПравку = async (id: string) => {
    if (module.dirty && !(await save({ оставитьПравку: true }))) return;
    setEditing((current) => ({ ...current, [id]: false }));
  };

  /**
   * Новый врач создаётся с ПУСТЫМ ФИО.
   *
   * Заготовку «Новый врач» приходилось сначала выделять и стирать, а
   * незамеченная заготовка попадала в наряд. Уведомления включены заранее:
   * это ожидаемое поведение, и всё равно они не работают без почты.
   */
  const addDoctor = () => {
    const id = useDrafts.getState().create('doctors', 'doctor', {
      name: '', clinics: [], phone: '', email: '',
      telegram: '', whatsapp: '', max: '', notify: true, note: '', archived: false, showPrices: false,
      lastName: '', firstName: '', middleName: '',
    });
    setOpenId(id);
    setEditing((current) => ({ ...current, [id]: true }));
    show('Врач добавлен — заполните ФИО и клиники');
  };

  /*
   * Рассылка НЕ ОТКРЫВАЕТСЯ поверх несохранённых правок.
   *
   * Письма разбирает сервер, и адреса он берёт из базы. Если почту врачу
   * только что дописали и ещё не нажали «Сохранить», письмо ушло бы не туда
   * или не ушло вовсе — а в окне адрес показывался бы правильный.
   */
  const openMailing = () => {
    if (module.dirty) {
      show('Сначала сохраните изменения — рассылка берёт адреса из базы', 'error');
      return;
    }
    setMailingOpen(true);
  };

  const openDoctor = openId ? doctors.find((d) => d.id === openId) : undefined;
  /*
   * Тот же врач, но КАК ЕГО ЗНАЕТ СЕРВЕР.
   *
   * Состояние кабинета (кому выдано приглашение, задан ли пароль, когда
   * заходил) в черновике раздела не живёт и жить не должно: это не правка
   * справочника, а дверь наружу, и меняется она отдельными запросами.
   * Только владельцу — сотруднику лаборатории эти сведения не показываются.
   */
  const серверныйВрач =
    bootstrap.data?.user.role === 'OWNER' && openId
      ? (bootstrap.data.doctors.find((d) => d.id === openId) ?? null)
      : null;
  const doctorToDelete = confirmDoctor ? doctors.find((d) => d.id === confirmDoctor) : undefined;

  /**
   * Закрытие карточки возвращает её в просмотр — иначе режим правки
   * запоминался бы за врачом и карточка открывалась сразу формой. Правки не
   * теряются: они живут в черновике раздела и ждут «Сохранить изменения».
   */
  const closeCard = () => {
    if (openId) setEditing((current) => ({ ...current, [openId]: false }));
    setOpenId(null);
  };

  /**
   * Врач с адресом, но с выключенными уведомлениями — молчащий.
   *
   * Такой врач писем не получает и ничем не отличается в списке от
   * врача вовсе без почты: адрес есть, а окно «Сообщить врачу» не появляется.
   * Пока таких было ноль, отдельная отметка была не нужна; после того как их
   * набралось несколько десятков, найти их без неё можно только открывая
   * карточки подряд.
   */
  const silent = (d: DoctorRow) => Boolean(d.email.trim()) && !d.notify;

  /*
   * Рабочий список и архив — раздельно. В архив уходит врач с заказами,
   * которого удалили: в старых заказах он остаётся, в списке мешать не должен.
   */
  const active = useMemo(() => doctors.filter((d) => !d.archived), [doctors]);
  const archivedCount = doctors.length - active.length;

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = showArchive ? doctors.filter((d) => d.archived) : active;
    const list = q ? base.filter((d) => d.name.toLowerCase().includes(q)) : base;
    return [...list].sort((a, b) => a.name.localeCompare(b.name, 'ru'));
  }, [doctors, active, query, showArchive]);

  const linked = active.filter((d) => d.clinics.length > 0).length;
  const clinicName = (id: string) => clinics.find((c) => c.id === id)?.name ?? '';

  /* Листается список, а не страница: шапка раздела остаётся на месте. */
  const { ref: списокRef, высота: высотаСписка } = useFillHeight();

  /* Масштаб плиток: у каждого раздела свой, запоминается в браузере. */
  const zoom = useTableZoom('doctors', true);

  if (bootstrap.isLoading) return <Spinner />;

  return (
    <div>
      <SectionHeader
        icon={DoctorIcon}
        title="Врачи"
        subtitle={`${active.length} врачей в базе · ${linked} с привязанной клиникой`}
        right={
          <>
            {/*
              Поиск и «Добавить» ОТДЕЛЕНЫ от сохранения.

              Это работа со списком: найти запись, завести новую. «Сохранить»
              и «Отменить» относятся ко всему разделу разом и стоят особняком —
              в общем ряду через одинаковый зазор четыре кнопки читались как
              равные, и рука шла не туда.
            */}
            {/*
              НА ТЕЛЕФОНЕ ЭТО ДВЕ СТРОКИ: поиск во всю ширину, кнопки под ним.

              В одну строку они не помещались: поле в 144 пикселя плюс три
              кнопки — и «Рассылка» уезжала за правый край экрана вместе со
              страницей. Сжимать поле ради этого тоже нельзя: при появлении
              «Сохранить» и «Отменить» оно схлопывалось в полоску, в которую
              не попасть пальцем.

              Кнопки собраны в свою обёртку, а не брошены в общий ряд: иначе
              «Сохранить» пристраивалось к «Рассылке», а «Отменить» уходило
              строкой ниже — пара разъезжалась. На широком экране всё это
              снимается и ряд снова один.
            */}
            <span
              className="flex basis-full flex-wrap items-center gap-2 md:mr-2 md:basis-auto md:flex-nowrap md:border-r md:pr-4"
              style={{ borderColor: 'var(--otc-line)' }}
            >
              {/*
                Лупа ВНУТРИ поля. Обёртка повторяет ширину самого поля —
                на телефоне во всю строку, на широком экране 11rem, — иначе
                значок уехал бы от края поля вместе с обёрткой по содержимому.
                Отступ под лупу строчным стилем: класс `pl-9` перебивается
                сокращённой записью `padding` из общего вида полей.
              */}
              <span className="relative block w-full min-w-0 md:w-44">
                <Search
                  size={14}
                  className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2"
                  style={{ color: 'var(--otc-ink2)' }}
                />
                <input
                  className="w-full rounded-lg border px-3 py-1.5 text-sm"
                  style={{
                    borderColor: 'var(--otc-line)',
                    background: 'var(--otc-surface)',
                    paddingLeft: '2.1rem',
                  }}
                  placeholder="Поиск…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </span>
              <span className="flex w-full items-center gap-2 md:w-auto">
                <Button onClick={addDoctor}>
                  <Plus size={14} /> Добавить
                </Button>
                {/*
                  «Рассылка» стоит рядом с поиском и «Добавить»: это тоже работа
                  со списком целиком, а не с одной карточкой. Кнопка появляется
                  только когда есть кому писать.
                */}
                {active.some((d) => d.email.trim()) && (
                  /* Белая кнопка с рамкой — как «Сводный отчёт» в «Заказах». */
                  <button
                    onClick={openMailing}
                    title="Написать всем врачам"
                    className="inline-flex items-center justify-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[13px] font-medium text-ink transition-colors hover:bg-black/5 md:px-3.5 md:py-2 md:text-sm"
                    style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-surface)' }}
                  >
                    <Send size={14} /> Рассылка
                  </button>
                )}
                {/* Архив виден, только когда в нём кто-то есть — или он уже открыт. */}
                {(archivedCount > 0 || showArchive) && (
                  <button
                    className="inline-flex items-center justify-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[13px] font-medium text-ink transition-colors hover:bg-black/5 md:px-3.5 md:py-2 md:text-sm"
                    style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-surface)' }}
                    onClick={() => setShowArchive((v) => !v)}
                    title={showArchive ? 'Вернуться к рабочему списку' : 'Врачи, убранные в архив'}
                  >
                    <Archive size={14} /> {showArchive ? 'К списку' : `Архив · ${archivedCount}`}
                  </button>
                )}
                {/* Масштаб — рядом с работой над списком: он тоже про список
                    целиком, а не про отдельную карточку. */}
                <ZoomControl zoom={zoom} hint="Мельче — на экран влезет больше врачей" />
              </span>
            </span>
            <SaveDiscard
              dirty={module.dirty}
              saving={module.saving}
              onSave={() => void save()}
              onDiscard={() => {
                module.discard();
                show('Изменения отменены');
              }}
              what="Врачи"
            />
          </>
        }
      />

      {/*
        Листаются ПЛИТКИ, а не страница: поиск, «Добавить врача» и «Рассылка»
        остаются на месте на любой прокрутке. Подробности — в `useFillHeight`.

        Небольшое поле по краям — чтобы тени плиток не срезались границей
        прокручиваемого блока.
      */}
      <div
        ref={списокRef}
        className="-mx-1 px-1"
        style={{ maxHeight: высотаСписка ?? undefined, overflowY: высотаСписка ? 'auto' : undefined }}
      >
        {/*
          Масштаб — на СОДЕРЖИМОМ, а не на прокручиваемом блоке: высота блока
          подобрана под окно, и `zoom` на нём сжал бы и её — список перестал
          бы доходить до низа экрана.
        */}
        <div style={{ zoom: `${zoom.zoom}%` }}>
        <CardGrid>
        {visible.map((doctor) => (
          <Card
            key={doctor.id}
            title={doctor.name || 'Без имени'}
            /* Повторный двойной щелчок по той же плитке закрывает карточку. */
            onOpen={() => setOpenId((id) => (id === doctor.id ? null : doctor.id))}
            onDelete={doctor.archived ? null : () => setConfirmDoctor(doctor.id)}
            deleteTitle="Удалить врача"
            extraAction={
              doctor.archived
                ? {
                    icon: ArchiveRestore,
                    title: 'Вернуть из архива',
                    onClick: () => {
                      update(doctor.id, { archived: false });
                      show(`«${doctor.name || 'без имени'}» вернётся в список — нажмите «Сохранить»`);
                    },
                  }
                : undefined
            }
          >
            <div className="mt-2 space-y-1">
              <CardFact icon={Phone} value={doctor.phone} empty="телефон не указан" />
              <CardFact icon={Mail} value={doctor.email} empty="почта не указана" breakAll />
            </div>

            {/*
              Клиники врача — зеркало врачей на плитке клиники. Именно связь
              определяет, куда можно оформить заказ (§1), поэтому она видна
              не открывая карточку.
            */}
            <CardLinks
              icon={ClinicIcon}
              names={doctor.clinics.map(clinicName).filter(Boolean)}
              empty="клиники не привязаны"
            />

            <CardChips>
              {/* Уведомления — осознанно включённая настройка, как напоминание
                  о счёте у клиники. Молчание отмечать незачем: это норма. */}
              {/* Статусы — полностью, теми же словами, что в открытой карточке. */}
              {doctor.notify && doctor.email && <Chip warn>уведомления включены</Chip>}
              {/* Почта есть, а письма не идут — это ошибка настройки, а не
                  норма: адрес заводили именно чтобы писать. */}
              {silent(doctor) && <Chip danger>уведомления выключены</Chip>}
              {/* МАКС — тоже мессенджер: в карточке он есть, значит, и здесь. */}
              {(doctor.telegram || doctor.whatsapp || doctor.max) && <Chip>мессенджеры</Chip>}
              {doctor.note.trim() && <Chip>заметка</Chip>}
              {doctor.archived && <Chip>в архиве</Chip>}
              {/* Отсутствие клиники красным НЕ помечается: заказ на такого врача
                  оформляется, в заказе ему доступны все клиники. Серая строка
                  «клиники не привязаны» выше это и так показывает. */}
            </CardChips>
          </Card>
        ))}
        </CardGrid>

        {visible.length === 0 && <p className="py-8 text-center text-sm text-ink2">Никого не нашли</p>}
        </div>
      </div>

      {/*
        Карточка врача — ПОДВИЖНОЕ ОКНО, как карточка заказа.

        Просмотр и правка — разные состояния карточки, с разными кнопками
        внизу окна.

        Прежде это было модальное окно с затемнением: список врачей под ним
        гас и не работал, а само окно стояло там, где решили за вас. Теперь
        карточку отодвигают и растягивают, а плитки позади остаются
        видимыми — по ним же обычно и сверяются, заводя нового врача.
      */}
      {openDoctor && (
        <CardWindow
          ключ="otc.doctorWindow.v2"
          /*
            В раскрытой карточке — ПОЛНОЕ ФИО: «Аюшеева Галина Сергеевна».
            Короткое («Аюшеева Г.С.») остаётся на плитках и в таблицах, где
            место считанное. Пока полное не заполнено, показывается короткое.
          */
          title={полноеФИО(openDoctor) || openDoctor.name || 'Новый врач'}
          onClose={closeCard}
          /* Шире прежних 820: текст в карточке стал крупнее. */
          ширина={960}
          footer={
            <>
              {/*
                Две кнопки, а не три: «Изменить» — начать правку, «Готово» —
                закончить и сохранить. Отдельной «Сохранить» здесь больше нет:
                она означала то же самое, что «Готово», и заставляла нажимать
                дважды. Кнопка в шапке раздела осталась — она сохраняет весь
                раздел разом, включая правки в других карточках.
              */}
              <Button
                variant={editing[openDoctor.id] ? 'primary' : 'secondary'}
                disabled={module.saving}
                onClick={() =>
                  editing[openDoctor.id]
                    ? void завершитьПравку(openDoctor.id)
                    : setEditing((e) => ({ ...e, [openDoctor.id]: true }))
                }
              >
                {editing[openDoctor.id] ? <Check size={14} /> : <Pencil size={14} />}
                {editing[openDoctor.id] ? (module.saving ? 'Сохранение…' : 'Готово') : 'Изменить'}
              </Button>

              <Button variant="ghost" onClick={closeCard}>Закрыть</Button>
            </>
          }
        >
          <div className="pb-4">
            {editing[openDoctor.id] ? (
              <DoctorEdit
                doctor={openDoctor}
                clinics={clinics}
                onUpdate={(patch) => update(openDoctor.id, patch)}
              />
            ) : (
              /*
                КАБИНЕТ ВРАЧА ОСТАНОВЛЕН (25.09.2026, решение владельца).

                Здесь стоял блок «Кабинет врача»: приглашение, дверь для
                предпросмотра, закрытие доступа. Пока модуль на стопе, двери
                в карточке нет — предлагать вход, которого не существует,
                нельзя.

                Код никуда не делся: DoctorPortalBlock и DoctorCabinetPreview
                лежат рядом и возвращаются вместе с PORTAL_ENABLED на сервере.
              */
              <DoctorView doctor={openDoctor} clinicNames={openDoctor.clinics.map(clinicName)} />
            )}
          </div>
        </CardWindow>
      )}

      {/* Подтверждение удаления — то же окно, что и в «Клиниках» */}
      {doctorToDelete && (
        <ConfirmDialog
          title={`Удалить врача «${doctorToDelete.name || 'без имени'}»?`}
          description="Врач без заказов исчезнет из справочника вместе с привязкой к клиникам."
          details={
            <>
              <p>Клиник привязано: {doctorToDelete.clinics.length}</p>
              <p className="mt-1 text-xs text-ink2">
                Если у врача есть заказы, он уйдёт в архив: из списков и выбора пропадёт, а в старых
                заказах фамилия останется. Вернуть можно из «Архива».
              </p>
            </>
          }
          confirmLabel="Удалить врача"
          onConfirm={() => {
            useDrafts.getState().remove('doctors', 'doctor', doctorToDelete.id);
            setConfirmDoctor(null);
            if (openId === doctorToDelete.id) closeCard();
            show(`Врач «${doctorToDelete.name || 'без имени'}» помечен к удалению — нажмите «Сохранить». С заказами уйдёт в архив`);
          }}
          onCancel={() => setConfirmDoctor(null)}
        />
      )}

      {module.conflicts && (
        <ConflictModal
          conflicts={module.conflicts}
          onReload={() => {
            module.discard();
            module.dismissConflicts();
            setEditing({});
            void bootstrap.refetch();
          }}
          onKeepMine={() => void module.keepMine()}
          onClose={module.dismissConflicts}
        />
      )}

      {/*
        Окно рассылки получает ЧЕРНОВИК списка, а не ответ сервера: если почту
        врачу только что дописали и ещё не сохранили, отправлять по ней рано —
        сервер о ней не знает. Поэтому несохранённые правки окно не пустят.
      */}
      {mailingOpen && (
        <MailingDialog
          doctors={active.map((d) => ({ id: d.id, name: d.name, email: d.email }))}
          onClose={() => setMailingOpen(false)}
          onDone={(sent, failed) =>
            show(
              failed === 0
                ? `Разослано писем: ${sent}`
                : `Разослано ${sent}, не доставлено ${failed}`,
              failed === 0 ? 'info' : 'error',
            )
          }
        />
      )}

      <Toast toast={toast} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Просмотр
// ---------------------------------------------------------------------------

function DoctorView({ doctor, clinicNames }: { doctor: DoctorRow; clinicNames: string[] }) {
  return (
    /* Крупный просмотр — см. .otc-card-view в index.css. */
    <div className="otc-card-view space-y-5">
      {/*
        Блок с данными — НА БЕЛОМ.

        Прежде он заливался общим фоном программы и внутри такого же
        светлого окна читался как пустое место, а не как отдельная справка.
        Белый лист в рамке отделяет данные от полей вокруг — так же, как
        белые плитки отделяются от фона в списке.
      */}
      <div className="rounded-lg border p-3" style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-surface)' }}>
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-ink">Связь</span>
          {/*
            Показывается и ВЫКЛЮЧЕННОЕ состояние. Раньше отметки просто не
            было, и молчание программы при заведении заказа выглядело
            поломкой: почта у врача указана, а окно не появляется.
          */}
          {/* Таблеткой, как пометки юрлица в карточке клиники. */}
          {doctor.email &&
            (doctor.notify ? (
              <Пометка цвет="var(--otc-amber)">уведомления включены</Пометка>
            ) : (
              <Пометка цвет="var(--otc-ink2)">уведомления выключены — письма не предлагаются</Пометка>
            ))}
        </div>

        <div className="grid gap-x-6 md:grid-cols-2">
          <div>
            {/* Полное ФИО — первым: к врачу обращаются именно так. */}
            {полноеФИО(doctor) && <Row label="ФИО" value={полноеФИО(doctor)} />}
            <Row label="Телефон" value={doctor.phone} muted strong />
            <Row label="Почта" value={doctor.email} href={`mailto:${doctor.email.split(/[,;\s]+/).filter(Boolean).join(',')}`} />
          </div>
          <div>
            <Row label="Telegram" value={doctor.telegram} strong />
            <Row label="МАКС" value={doctor.max} />
          </div>
        </div>

        {/* Написать — прямо отсюда, без переписывания номера руками. */}
        <div className="mt-2">
          <Messengers contact={doctor} />
        </div>
      </div>

      <div>
        <p className="mb-2 text-sm font-semibold text-ink">
          Клиники
          {clinicNames.length > 0 && (
            <span className="ml-1.5 text-xs font-normal text-ink2">· {clinicNames.length}</span>
          )}
        </p>
        <div className="flex flex-wrap gap-2">
          {clinicNames.map((name) => (
            <span
              key={name}
              className="rounded-full border px-3 py-1 text-xs"
              style={{
                borderColor: 'var(--otc-line)',
                background: 'var(--otc-surface)',
                color: 'var(--otc-ink)',
              }}
            >
              {name}
            </span>
          ))}
          {/*
            Пустая привязка — не ошибка. Прежде здесь стояло красное «заказ на
            этого врача оформить нельзя», и это было неправдой: врачу без
            клиники в заказе доступны все клиники. Остаётся спокойная пометка.
          */}
          {clinicNames.length === 0 && <span className="text-xs text-ink2">Клиники не привязаны</span>}
        </div>
      </div>

      {doctor.note.trim() && (
        <div>
          <p className="mb-1.5 flex items-center gap-1.5 text-sm font-semibold text-ink">
            <StickyNote size={14} className="text-ink2" /> Заметка
          </p>
          <p
            className="whitespace-pre-wrap rounded-lg border p-3 text-xs text-ink"
            style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-surface)' }}
          >
            {doctor.note}
          </p>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Правка
// ---------------------------------------------------------------------------

function DoctorEdit({
  doctor,
  clinics,
  onUpdate,
}: {
  doctor: DoctorRow;
  clinics: { id: string; name: string }[];
  onUpdate: (patch: Partial<DoctorRow>) => void;
}) {
  return (
    <div className="space-y-4">
      {/*
        ФИО — ТРЕМЯ ПОЛЯМИ, отдельного поля «ФИО» больше нет.

        Короткое имя («Аюшеева Г.С.») собирается из них само: им подписаны
        заказы, счета, письма и плитки, и вводить его руками вторым разом
        значило бы однажды разойтись с фамилией в соседнем поле.

        Отдельные поля, а не одна строка: разбирать «Аюшеева Галина
        Сергеевна» обратно — гадание, особенно с двойными фамилиями.
      */}
      <div className="grid gap-3 md:grid-cols-3">
        <Field label="Фамилия" hint={`В списках запишется как «${короткоеИмя(doctor) || 'Фамилия И.О.'}»`}>
          <input
            className="otc-input"
            value={doctor.lastName ?? ''}
            onChange={(e) => обновитьФИО(doctor, onUpdate, { lastName: e.target.value })}
            placeholder="Аюшеева"
          />
        </Field>
        <Field label="Имя">
          <input
            className="otc-input"
            value={doctor.firstName ?? ''}
            onChange={(e) => обновитьФИО(doctor, onUpdate, { firstName: e.target.value })}
            placeholder="Галина"
          />
        </Field>
        <Field label="Отчество">
          <input
            className="otc-input"
            value={doctor.middleName ?? ''}
            onChange={(e) => обновитьФИО(doctor, onUpdate, { middleName: e.target.value })}
            placeholder="Сергеевна"
          />
        </Field>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <Field label="Клиники" hint="Определяет, куда можно оформить заказ этого врача">
          <ClinicPicker
            clinics={clinics as never}
            selected={doctor.clinics}
            onChange={(next) => onUpdate({ clinics: next })}
          />
        </Field>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        {/*
          Телефон приводится к общему виду, КОГДА ИЗ ПОЛЯ УХОДЯТ.

          Не на каждой букве: пока набирают, подстановка дефисов прыгала бы
          под курсором и мешала стирать. А набирают по-разному — с восьмёрки,
          слитно, в скобках, — и в списке потом три написания одного формата.
          Правило живёт в общем пакете (`formatPhone`) и покрыто проверками:
          российский номер становится «+7 914 928-69-66», чужой остаётся как
          есть.
        */}
        <Field label="Телефон" hint="Запишется как +7 914 928-69-66">
          <input
            className="otc-input tabular"
            value={doctor.phone}
            onChange={(e) => onUpdate({ phone: e.target.value })}
            onBlur={(e) => {
              const приведён = formatPhone(e.target.value);
              if (приведён !== doctor.phone) onUpdate({ phone: приведён });
            }}
          />
        </Field>
        <Field label="Почта" hint="Без неё уведомления не работают">
          <input
            className="otc-input"
            value={doctor.email}
            onChange={(e) => onUpdate({ email: e.target.value })}
          />
        </Field>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <Field label="Telegram">
          <input
            className="otc-input"
            value={doctor.telegram}
            onChange={(e) => onUpdate({ telegram: e.target.value })}
          />
        </Field>
        {/*
          Поля WhatsApp ЗДЕСЬ НЕТ.

          Ников у него не бывает: человека находят по номеру телефона, и ярлык
          WhatsApp строит ссылку из телефона врача сам. Поле стояло пустым у
          всех врачей и только занимало место. Сами данные в базе остались —
          если у кого-то окажется отдельный номер для WhatsApp, поле вернётся.
        */}
        <Field
          label="МАКС"
          hint="Адрес чата из веб-версии: откройте переписку на web.max.ru и вставьте адрес вида web.max.ru/c/123456. Годится и личная ссылка max.ru/u/…"
        >
          <input
            className="otc-input"
            value={doctor.max}
            onChange={(e) => onUpdate({ max: e.target.value })}
          />
        </Field>
      </div>

      <Field label="Заметка" hint="Здесь лежат подсказки из исходных данных лаборатории">
        <textarea
          className="otc-input"
          rows={3}
          value={doctor.note}
          onChange={(e) => onUpdate({ note: e.target.value })}
        />
      </Field>

      {/*
        Плашка, а не системный флажок — как «Предупреждать, что пора выставить
        счёт» у юрлица. Без почты переключатель бессмыслен, поэтому вместо него
        показана причина: иначе он выглядел бы просто сломанным.
      */}
      {doctor.email.trim() ? (
        <ToggleSetting
          checked={doctor.notify}
          onChange={(next) => onUpdate({ notify: next })}
          label="Уведомлять о заказах"
          // Писем ДВА, и переключатель управляет обоими: раньше подпись
          // называла только готовность, и выключенным он выглядел так, будто
          // письмо о приёме всё равно уйдёт.
          hint={`Письма о приёме и о готовности — на ${doctor.email}. Каждое перед отправкой показывается`}
          accent="var(--otc-amber)"
        />
      ) : (
        <p
          className="flex items-center gap-2 rounded-xl border border-dashed px-3.5 py-3 text-xs text-ink2"
          style={{ borderColor: 'var(--otc-line)' }}
        >
          <MessageCircle size={14} className="flex-shrink-0 opacity-60" />
          Уведомления настраиваются после того, как указана почта — сейчас отправлять некуда.
        </p>
      )}

      {/*
        Признак «видит цены в кабинете» остался в базе, а переключателя нет:
        кабинет врача остановлен 25.09.2026, настраивать в нём нечего.
      */}
    </div>
  );
}
